import { defineCustomElements } from '@phonero/pux-core/loader';
import { defineCustomElements as defineCustomElements$1 } from '@phonero/pux-icons/loader';
import { defineCustomElements as defineCustomElements$2 } from '@phonero/pux-logos/loader';
import React__default, { createElement, createRef, useContext } from 'react';
import '@phonero/pux-core';

const dashToPascalCase$1 = (str) => str
    .toLowerCase()
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
const camelToDashCase$1 = (str) => str.replace(/([A-Z])/g, (m) => `-${m[0].toLowerCase()}`);

const attachProps$1 = (node, newProps, oldProps = {}) => {
    // some test frameworks don't render DOM elements, so we test here to make sure we are dealing with DOM first
    if (node instanceof Element) {
        // add any classes in className to the class list
        const className = getClassName$1(node.classList, newProps, oldProps);
        if (className !== '') {
            node.className = className;
        }
        Object.keys(newProps).forEach((name) => {
            if (name === 'children' ||
                name === 'style' ||
                name === 'ref' ||
                name === 'class' ||
                name === 'className' ||
                name === 'forwardedRef') {
                return;
            }
            if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                const eventName = name.substring(2);
                const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
                if (!isCoveredByReact$1(eventNameLc)) {
                    syncEvent$1(node, eventNameLc, newProps[name]);
                }
            }
            else {
                node[name] = newProps[name];
                const propType = typeof newProps[name];
                if (propType === 'string') {
                    node.setAttribute(camelToDashCase$1(name), newProps[name]);
                }
            }
        });
    }
};
const getClassName$1 = (classList, newProps, oldProps) => {
    const newClassProp = newProps.className || newProps.class;
    const oldClassProp = oldProps.className || oldProps.class;
    // map the classes to Maps for performance
    const currentClasses = arrayToMap$1(classList);
    const incomingPropClasses = arrayToMap$1(newClassProp ? newClassProp.split(' ') : []);
    const oldPropClasses = arrayToMap$1(oldClassProp ? oldClassProp.split(' ') : []);
    const finalClassNames = [];
    // loop through each of the current classes on the component
    // to see if it should be a part of the classNames added
    currentClasses.forEach((currentClass) => {
        if (incomingPropClasses.has(currentClass)) {
            // add it as its already included in classnames coming in from newProps
            finalClassNames.push(currentClass);
            incomingPropClasses.delete(currentClass);
        }
        else if (!oldPropClasses.has(currentClass)) {
            // add it as it has NOT been removed by user
            finalClassNames.push(currentClass);
        }
    });
    incomingPropClasses.forEach((s) => finalClassNames.push(s));
    return finalClassNames.join(' ');
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact$1 = (eventNameSuffix) => {
    if (typeof document === 'undefined') {
        return true;
    }
    else {
        const eventName = 'on' + eventNameSuffix;
        let isSupported = eventName in document;
        if (!isSupported) {
            const element = document.createElement('div');
            element.setAttribute(eventName, 'return;');
            isSupported = typeof element[eventName] === 'function';
        }
        return isSupported;
    }
};
const syncEvent$1 = (node, eventName, newEventHandler) => {
    const eventStore = node.__events || (node.__events = {});
    const oldEventHandler = eventStore[eventName];
    // Remove old listener so they don't double up.
    if (oldEventHandler) {
        node.removeEventListener(eventName, oldEventHandler);
    }
    // Bind new listener.
    node.addEventListener(eventName, (eventStore[eventName] = function handler(e) {
        if (newEventHandler) {
            newEventHandler.call(this, e);
        }
    }));
};
const arrayToMap$1 = (arr) => {
    const map = new Map();
    arr.forEach((s) => map.set(s, s));
    return map;
};

const setRef$1 = (ref, value) => {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref != null) {
        // Cast as a MutableRef so we can assign current
        ref.current = value;
    }
};
const mergeRefs = (...refs) => {
    return (value) => {
        refs.forEach(ref => {
            setRef$1(ref, value);
        });
    };
};
const createForwardRef$1 = (ReactComponent, displayName) => {
    const forwardRef = (props, ref) => {
        return React__default.createElement(ReactComponent, Object.assign({}, props, { forwardedRef: ref }));
    };
    forwardRef.displayName = displayName;
    return React__default.forwardRef(forwardRef);
};

var __rest$6 = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
const createReactComponent$1 = (tagName, ReactComponentContext, manipulatePropsFunction, defineCustomElement) => {
    if (defineCustomElement !== undefined) {
        defineCustomElement();
    }
    const displayName = dashToPascalCase$1(tagName);
    const ReactComponent = class extends React__default.Component {
        constructor(props) {
            super(props);
            this.setComponentElRef = (element) => {
                this.componentEl = element;
            };
        }
        componentDidMount() {
            this.componentDidUpdate(this.props);
        }
        componentDidUpdate(prevProps) {
            attachProps$1(this.componentEl, this.props, prevProps);
        }
        render() {
            const _a = this.props, { children, forwardedRef, style, className, ref } = _a, cProps = __rest$6(_a, ["children", "forwardedRef", "style", "className", "ref"]);
            let propsToPass = Object.keys(cProps).reduce((acc, name) => {
                const value = cProps[name];
                if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                    const eventName = name.substring(2).toLowerCase();
                    if (typeof document !== 'undefined' && isCoveredByReact$1(eventName)) {
                        acc[name] = value;
                    }
                }
                else {
                    // we should only render strings, booleans, and numbers as attrs in html.
                    // objects, functions, arrays etc get synced via properties on mount.
                    const type = typeof value;
                    if (type === 'string' || type === 'boolean' || type === 'number') {
                        acc[camelToDashCase$1(name)] = value;
                    }
                }
                return acc;
            }, {});
            if (manipulatePropsFunction) {
                propsToPass = manipulatePropsFunction(this.props, propsToPass);
            }
            const newProps = Object.assign(Object.assign({}, propsToPass), { ref: mergeRefs(forwardedRef, this.setComponentElRef), style });
            /**
             * We use createElement here instead of
             * React.createElement to work around a
             * bug in Vite (https://github.com/vitejs/vite/issues/6104).
             * React.createElement causes all elements to be rendered
             * as <tagname> instead of the actual Web Component.
             */
            return createElement(tagName, newProps, children);
        }
        static get displayName() {
            return displayName;
        }
    };
    // If context was passed to createReactComponent then conditionally add it to the Component Class
    if (ReactComponentContext) {
        ReactComponent.contextType = ReactComponentContext;
    }
    return createForwardRef$1(ReactComponent, displayName);
};

/* eslint-disable */
defineCustomElements();
const PuxAccordian = /*@__PURE__*/ createReactComponent$1('pux-accordian');
const PuxAccordianPanel = /*@__PURE__*/ createReactComponent$1('pux-accordian-panel');
const PuxActionButton = /*@__PURE__*/ createReactComponent$1('pux-action-button');
const PuxApp = /*@__PURE__*/ createReactComponent$1('pux-app');
const PuxAvatar = /*@__PURE__*/ createReactComponent$1('pux-avatar');
const PuxBackdrop = /*@__PURE__*/ createReactComponent$1('pux-backdrop');
const PuxBadge = /*@__PURE__*/ createReactComponent$1('pux-badge');
const PuxButton = /*@__PURE__*/ createReactComponent$1('pux-button');
const PuxButtons = /*@__PURE__*/ createReactComponent$1('pux-buttons');
const PuxCard = /*@__PURE__*/ createReactComponent$1('pux-card');
const PuxCardContent = /*@__PURE__*/ createReactComponent$1('pux-card-content');
const PuxCardHeader = /*@__PURE__*/ createReactComponent$1('pux-card-header');
const PuxCardSubtitle = /*@__PURE__*/ createReactComponent$1('pux-card-subtitle');
const PuxCardTitle = /*@__PURE__*/ createReactComponent$1('pux-card-title');
const PuxChart = /*@__PURE__*/ createReactComponent$1('pux-chart');
const PuxCheckbox = /*@__PURE__*/ createReactComponent$1('pux-checkbox');
const PuxChip = /*@__PURE__*/ createReactComponent$1('pux-chip');
const PuxCol = /*@__PURE__*/ createReactComponent$1('pux-col');
const PuxContent = /*@__PURE__*/ createReactComponent$1('pux-content');
const PuxDatetime = /*@__PURE__*/ createReactComponent$1('pux-datetime');
const PuxDialogTitle = /*@__PURE__*/ createReactComponent$1('pux-dialog-title');
const PuxDropdown = /*@__PURE__*/ createReactComponent$1('pux-dropdown');
const PuxFab = /*@__PURE__*/ createReactComponent$1('pux-fab');
const PuxFabButton = /*@__PURE__*/ createReactComponent$1('pux-fab-button');
const PuxFabList = /*@__PURE__*/ createReactComponent$1('pux-fab-list');
const PuxFooter = /*@__PURE__*/ createReactComponent$1('pux-footer');
const PuxGauge = /*@__PURE__*/ createReactComponent$1('pux-gauge');
const PuxGrid = /*@__PURE__*/ createReactComponent$1('pux-grid');
const PuxHeader = /*@__PURE__*/ createReactComponent$1('pux-header');
const PuxImg = /*@__PURE__*/ createReactComponent$1('pux-img');
const PuxInfiniteScroll = /*@__PURE__*/ createReactComponent$1('pux-infinite-scroll');
const PuxInfiniteScrollContent = /*@__PURE__*/ createReactComponent$1('pux-infinite-scroll-content');
const PuxInput = /*@__PURE__*/ createReactComponent$1('pux-input');
const PuxItemDivider = /*@__PURE__*/ createReactComponent$1('pux-item-divider');
const PuxItemGroup = /*@__PURE__*/ createReactComponent$1('pux-item-group');
const PuxLabel = /*@__PURE__*/ createReactComponent$1('pux-label');
const PuxList = /*@__PURE__*/ createReactComponent$1('pux-list');
const PuxListHeader = /*@__PURE__*/ createReactComponent$1('pux-list-header');
const PuxMenu = /*@__PURE__*/ createReactComponent$1('pux-menu');
const PuxMenuButton = /*@__PURE__*/ createReactComponent$1('pux-menu-button');
const PuxMenuToggle = /*@__PURE__*/ createReactComponent$1('pux-menu-toggle');
const PuxModalContent = /*@__PURE__*/ createReactComponent$1('pux-modal-content');
const PuxNav = /*@__PURE__*/ createReactComponent$1('pux-nav');
const PuxNavlinks = /*@__PURE__*/ createReactComponent$1('pux-navlinks');
const PuxNote = /*@__PURE__*/ createReactComponent$1('pux-note');
const PuxPageOutlet = /*@__PURE__*/ createReactComponent$1('pux-page-outlet');
const PuxProgressBar = /*@__PURE__*/ createReactComponent$1('pux-progress-bar');
const PuxRadio = /*@__PURE__*/ createReactComponent$1('pux-radio');
const PuxRadioGroup = /*@__PURE__*/ createReactComponent$1('pux-radio-group');
const PuxRange = /*@__PURE__*/ createReactComponent$1('pux-range');
const PuxRefresher = /*@__PURE__*/ createReactComponent$1('pux-refresher');
const PuxRefresherContent = /*@__PURE__*/ createReactComponent$1('pux-refresher-content');
const PuxRow = /*@__PURE__*/ createReactComponent$1('pux-row');
const PuxSearchbar = /*@__PURE__*/ createReactComponent$1('pux-searchbar');
const PuxSegment = /*@__PURE__*/ createReactComponent$1('pux-segment');
const PuxSegmentButton = /*@__PURE__*/ createReactComponent$1('pux-segment-button');
const PuxSelect = /*@__PURE__*/ createReactComponent$1('pux-select');
const PuxSelectOption = /*@__PURE__*/ createReactComponent$1('pux-select-option');
const PuxSelectPopover = /*@__PURE__*/ createReactComponent$1('pux-select-popover');
const PuxSheet = /*@__PURE__*/ createReactComponent$1('pux-sheet');
const PuxSimcard = /*@__PURE__*/ createReactComponent$1('pux-simcard');
const PuxSkeletonText = /*@__PURE__*/ createReactComponent$1('pux-skeleton-text');
const PuxSlide = /*@__PURE__*/ createReactComponent$1('pux-slide');
const PuxSlides = /*@__PURE__*/ createReactComponent$1('pux-slides');
const PuxSpinner = /*@__PURE__*/ createReactComponent$1('pux-spinner');
const PuxSplitPane = /*@__PURE__*/ createReactComponent$1('pux-split-pane');
const PuxTable = /*@__PURE__*/ createReactComponent$1('pux-table');
const PuxText = /*@__PURE__*/ createReactComponent$1('pux-text');
const PuxTextarea = /*@__PURE__*/ createReactComponent$1('pux-textarea');
const PuxThumbnail = /*@__PURE__*/ createReactComponent$1('pux-thumbnail');
const PuxTitle = /*@__PURE__*/ createReactComponent$1('pux-title');
const PuxToggle = /*@__PURE__*/ createReactComponent$1('pux-toggle');
const PuxToolbar = /*@__PURE__*/ createReactComponent$1('pux-toolbar');
const PuxVirtualScroll = /*@__PURE__*/ createReactComponent$1('pux-virtual-scroll');

/* eslint-disable */
defineCustomElements$1();
const PuxIcon = /*@__PURE__*/ createReactComponent$1('pux-icon');

/* eslint-disable */
defineCustomElements$2();
const PuxLogo = /*@__PURE__*/ createReactComponent$1('pux-logo');

const dashToPascalCase = (str) => str
    .toLowerCase()
    .split("-")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join("");
const camelToDashCase = (str) => str.replace(/([A-Z])/g, (m) => `-${m[0].toLowerCase()}`);

const attachProps = (node, newProps, oldProps = {}) => {
    // some test frameworks don't render DOM elements, so we test here to make sure we are dealing with DOM first
    if (node instanceof Element) {
        // add any classes in className to the class list
        const className = getClassName(node.classList, newProps, oldProps);
        if (className !== "") {
            node.className = className;
        }
        Object.keys(newProps).forEach((name) => {
            if (name === "children" ||
                name === "style" ||
                name === "ref" ||
                name === "class" ||
                name === "className" ||
                name === "forwardedRef") {
                return;
            }
            if (name.indexOf("on") === 0 && name[2] === name[2].toUpperCase()) {
                const eventName = name.substring(2);
                const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
                if (!isCoveredByReact(eventNameLc)) {
                    syncEvent(node, eventNameLc, newProps[name]);
                }
            }
            else {
                const propType = typeof newProps[name];
                if (propType === "string") {
                    node.setAttribute(camelToDashCase(name), newProps[name]);
                }
                else {
                    node[name] = newProps[name];
                }
            }
        });
    }
};
const getClassName = (classList, newProps, oldProps) => {
    const newClassProp = newProps.className || newProps.class;
    const oldClassProp = oldProps.className || oldProps.class;
    // map the classes to Maps for performance
    const currentClasses = arrayToMap(classList);
    const incomingPropClasses = arrayToMap(newClassProp ? newClassProp.split(" ") : []);
    const oldPropClasses = arrayToMap(oldClassProp ? oldClassProp.split(" ") : []);
    const finalClassNames = [];
    // loop through each of the current classes on the component
    // to see if it should be a part of the classNames added
    currentClasses.forEach((currentClass) => {
        if (incomingPropClasses.has(currentClass)) {
            // add it as its already included in classnames coming in from newProps
            finalClassNames.push(currentClass);
            incomingPropClasses.delete(currentClass);
        }
        else if (!oldPropClasses.has(currentClass)) {
            // add it as it has NOT been removed by user
            finalClassNames.push(currentClass);
        }
    });
    incomingPropClasses.forEach((s) => finalClassNames.push(s));
    return finalClassNames.join(" ");
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact = (eventNameSuffix) => {
    if (typeof document === "undefined") {
        return true;
    }
    else {
        const eventName = "on" + eventNameSuffix;
        let isSupported = eventName in document;
        if (!isSupported) {
            const element = document.createElement("div");
            element.setAttribute(eventName, "return;");
            isSupported = typeof element[eventName] === "function";
        }
        return isSupported;
    }
};
const syncEvent = (node, eventName, newEventHandler) => {
    const eventStore = node.__events || (node.__events = {});
    const oldEventHandler = eventStore[eventName];
    // Remove old listener so they don't double up.
    if (oldEventHandler) {
        node.removeEventListener(eventName, oldEventHandler);
    }
    // Bind new listener.
    node.addEventListener(eventName, (eventStore[eventName] = function handler(e) {
        if (newEventHandler) {
            newEventHandler.call(this, e);
        }
    }));
};
const arrayToMap = (arr) => {
    const map = new Map();
    arr.forEach((s) => map.set(s, s));
    return map;
};

const createForwardRef = (ReactComponent, displayName) => {
    const forwardRef = (props, ref) => {
        return React__default.createElement(ReactComponent, Object.assign({}, props, { forwardedRef: ref }));
    };
    forwardRef.displayName = displayName;
    return React__default.forwardRef(forwardRef);
};

const NavContext = /*@__PURE__*/ React__default.createContext({
    getPuxRedirect: () => undefined,
    getPuxRoute: () => undefined,
    getPageManager: () => undefined,
    getStackManager: () => undefined,
    goBack: (route) => {
        if (typeof window !== "undefined") {
            if (typeof route === "string") {
                window.location.pathname = route;
            }
            else {
                window.history.back();
            }
        }
    },
    navigate: (path) => {
        if (typeof window !== "undefined") {
            window.location.pathname = path;
        }
    },
    hasPuxRouter: () => false,
    routeInfo: undefined,
    setCurrentTab: () => undefined,
    changeTab: (_tab, path) => {
        if (typeof window !== "undefined") {
            window.location.pathname = path;
        }
    },
    resetTab: (_tab, path) => {
        if (typeof window !== "undefined") {
            window.location.pathname = path;
        }
    },
});

const PuxLifeCycleContext = 
/*@__PURE__*/ React__default.createContext({
    onPuxViewWillEnter: () => {
        return;
    },
    puxViewWillEnter: () => {
        return;
    },
    onPuxViewDidEnter: () => {
        return;
    },
    puxViewDidEnter: () => {
        return;
    },
    onPuxViewWillLeave: () => {
        return;
    },
    puxViewWillLeave: () => {
        return;
    },
    onPuxViewDidLeave: () => {
        return;
    },
    puxViewDidLeave: () => {
        return;
    },
});
const DefaultPuxLifeCycleContext = class {
    constructor() {
        this.puxViewWillEnterCallbacks = [];
        this.puxViewDidEnterCallbacks = [];
        this.puxViewWillLeaveCallbacks = [];
        this.puxViewDidLeaveCallbacks = [];
    }
    onPuxViewWillEnter(callback) {
        if (callback.id) {
            const index = this.puxViewWillEnterCallbacks.findIndex((x) => x.id === callback.id);
            if (index > -1) {
                this.puxViewWillEnterCallbacks[index] = callback;
            }
            else {
                this.puxViewWillEnterCallbacks.push(callback);
            }
        }
        else {
            this.puxViewWillEnterCallbacks.push(callback);
        }
    }
    puxViewWillEnter() {
        this.puxViewWillEnterCallbacks.forEach((cb) => cb());
    }
    onPuxViewDidEnter(callback) {
        if (callback.id) {
            const index = this.puxViewDidEnterCallbacks.findIndex((x) => x.id === callback.id);
            if (index > -1) {
                this.puxViewDidEnterCallbacks[index] = callback;
            }
            else {
                this.puxViewDidEnterCallbacks.push(callback);
            }
        }
        else {
            this.puxViewDidEnterCallbacks.push(callback);
        }
    }
    puxViewDidEnter() {
        this.puxViewDidEnterCallbacks.forEach((cb) => cb());
    }
    onPuxViewWillLeave(callback) {
        if (callback.id) {
            const index = this.puxViewWillLeaveCallbacks.findIndex((x) => x.id === callback.id);
            if (index > -1) {
                this.puxViewWillLeaveCallbacks[index] = callback;
            }
            else {
                this.puxViewWillLeaveCallbacks.push(callback);
            }
        }
        else {
            this.puxViewWillLeaveCallbacks.push(callback);
        }
    }
    puxViewWillLeave() {
        this.puxViewWillLeaveCallbacks.forEach((cb) => cb());
    }
    onPuxViewDidLeave(callback) {
        if (callback.id) {
            const index = this.puxViewDidLeaveCallbacks.findIndex((x) => x.id === callback.id);
            if (index > -1) {
                this.puxViewDidLeaveCallbacks[index] = callback;
            }
            else {
                this.puxViewDidLeaveCallbacks.push(callback);
            }
        }
        else {
            this.puxViewDidLeaveCallbacks.push(callback);
        }
    }
    puxViewDidLeave() {
        this.puxViewDidLeaveCallbacks.forEach((cb) => cb());
        this.componentCanBeDestroyed();
    }
    onComponentCanBeDestroyed(callback) {
        this.componentCanBeDestroyedCallback = callback;
    }
    componentCanBeDestroyed() {
        if (this.componentCanBeDestroyedCallback) {
            this.componentCanBeDestroyedCallback();
        }
    }
};

const StackContext = React__default.createContext({
    registerPuxPage: () => undefined,
    isInOutlet: () => false,
});

var __rest$5 = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
class PageManager extends React__default.PureComponent {
    constructor(props) {
        super(props);
        this.puxPageElementRef = this.props.forwardedRef || React__default.createRef();
    }
    componentDidMount() {
        if (this.puxPageElementRef.current) {
            if (this.context.isInOutlet()) {
                this.puxPageElementRef.current.classList.add("pux-page-invisible");
            }
            this.context.registerPuxPage(this.puxPageElementRef.current, this.props.routeInfo);
            this.puxPageElementRef.current.addEventListener("puxViewWillEnter", this.puxViewWillEnterHandler.bind(this));
            this.puxPageElementRef.current.addEventListener("puxViewDidEnter", this.puxViewDidEnterHandler.bind(this));
            this.puxPageElementRef.current.addEventListener("puxViewWillLeave", this.puxViewWillLeaveHandler.bind(this));
            this.puxPageElementRef.current.addEventListener("puxViewDidLeave", this.puxViewDidLeaveHandler.bind(this));
        }
    }
    componentWillUnmount() {
        if (this.puxPageElementRef.current) {
            this.puxPageElementRef.current.removeEventListener("puxViewWillEnter", this.puxViewWillEnterHandler.bind(this));
            this.puxPageElementRef.current.removeEventListener("puxViewDidEnter", this.puxViewDidEnterHandler.bind(this));
            this.puxPageElementRef.current.removeEventListener("puxViewWillLeave", this.puxViewWillLeaveHandler.bind(this));
            this.puxPageElementRef.current.removeEventListener("puxViewDidLeave", this.puxViewDidLeaveHandler.bind(this));
        }
    }
    puxViewWillEnterHandler() {
        this.puxLifeCycleContext.puxViewWillEnter();
    }
    puxViewDidEnterHandler() {
        this.puxLifeCycleContext.puxViewDidEnter();
    }
    puxViewWillLeaveHandler() {
        this.puxLifeCycleContext.puxViewWillLeave();
    }
    puxViewDidLeaveHandler() {
        this.puxLifeCycleContext.puxViewDidLeave();
    }
    render() {
        const _a = this.props, { className, children, routeInfo, forwardedRef } = _a, props = __rest$5(_a, ["className", "children", "routeInfo", "forwardedRef"]);
        return (React__default.createElement(PuxLifeCycleContext.Consumer, null, (context) => {
            this.puxLifeCycleContext = context;
            return (React__default.createElement("div", Object.assign({ className: className ? `${className} pux-page` : `pux-page`, ref: this.puxPageElementRef }, props), children));
        }));
    }
    static get contextType() {
        return StackContext;
    }
}
var PageManager$1 = PageManager;

var __rest$4 = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
class PuxPageInternal extends React__default.Component {
    constructor(props) {
        super(props);
    }
    render() {
        const _a = this.props, { className, children, forwardedRef } = _a, props = __rest$4(_a, ["className", "children", "forwardedRef"]);
        return this.context.hasPuxRouter() ? (React__default.createElement(PageManager$1, Object.assign({ className: className ? `${className}` : "", routeInfo: this.context.routeInfo, forwardedRef: forwardedRef }, props), children)) : (React__default.createElement("div", Object.assign({ className: className ? `pux-page ${className}` : "pux-page", ref: forwardedRef }, props), children));
    }
    static get displayName() {
        return "PuxPage";
    }
    static get contextType() {
        return NavContext;
    }
}
const PuxPage = createForwardRef(PuxPageInternal, "PuxPage");

var __rest$3 = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
const createReactComponent = (tagName) => {
    const displayName = dashToPascalCase(tagName);
    const cRef = createRef();
    const ReactComponent = class extends React__default.Component {
        constructor(props) {
            super(props);
        }
        componentDidMount() {
            this.componentDidUpdate(this.props);
        }
        componentDidUpdate(prevProps) {
            const node = cRef.current;
            if (!node) {
                return;
            }
            attachProps(node, this.props, prevProps);
        }
        render() {
            const _a = this.props, { children, forwardedRef, style, className, ref } = _a, cProps = __rest$3(_a, ["children", "forwardedRef", "style", "className", "ref"]);
            const propsToPass = Object.keys(cProps).reduce((acc, name) => {
                if (name.indexOf("on") === 0 && name[2] === name[2].toUpperCase()) {
                    const eventName = name.substring(2).toLowerCase();
                    if (isCoveredByReact(eventName)) {
                        acc[name] = cProps[name];
                    }
                }
                else if (typeof cProps[name] === "string") {
                    acc[camelToDashCase(name)] = cProps[name];
                }
                return acc;
            }, {});
            const newProps = Object.assign(Object.assign({}, propsToPass), { ref: (el) => {
                    setRef(forwardedRef, el);
                    setRef(cRef, el);
                }, style });
            return React__default.createElement(tagName, newProps, children);
        }
        static get displayName() {
            return displayName;
        }
    };
    return createForwardRef(ReactComponent, displayName);
};
function setRef(ref, value) {
    if (typeof ref === "function") {
        ref(value);
    }
    else if (ref) {
        ref.current = value;
    }
}

const PuxTabButtonInner = /*@__PURE__*/ createReactComponent("pux-tab-button");
const PuxTabBarInner = /*@__PURE__*/ createReactComponent("pux-tab-bar");
const PuxItemInner = /*@__PURE__*/ createReactComponent("pux-item");
const PuxBackButtonInner = /*@__PURE__*/ createReactComponent("pux-back-button");
const PuxRouterOutletInner = /*@__PURE__*/ createReactComponent("pux-router-outlet");

var __rest$2 = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
class PuxTabButton extends React__default.Component {
    constructor(props) {
        super(props);
        this.handlePuxTabButtonClick = this.handlePuxTabButtonClick.bind(this);
    }
    handlePuxTabButtonClick() {
        if (this.props.onClick) {
            this.props.onClick(new CustomEvent("puxTabButtonClick", {
                detail: {
                    tab: this.props.tab,
                    href: this.props.href,
                    routeOptions: this.props.routerOptions,
                },
            }));
        }
    }
    render() {
        const _a = this.props, rest = __rest$2(_a, ["onClick"]);
        return (React__default.createElement(PuxTabButtonInner, Object.assign({ onPuxTabButtonClick: this.handlePuxTabButtonClick }, rest)));
    }
    static get displayName() {
        return "PuxTabButton";
    }
}

var __rest$1 = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
class PuxTabBarUnwrapped extends React__default.PureComponent {
    constructor(props) {
        super(props);
        this.setActiveTabOnContext = (_tab) => { };
        const tabs = {};
        React__default.Children.forEach(props.children, (child) => {
            var _a, _b, _c, _d;
            if (child != null &&
                typeof child === "object" &&
                child.props &&
                (child.type === PuxTabButton || child.type.isTabButton)) {
                tabs[child.props.tab] = {
                    originalHref: child.props.href,
                    currentHref: child.props.href,
                    originalRouteOptions: child.props.href === ((_a = props.routeInfo) === null || _a === void 0 ? void 0 : _a.pathname)
                        ? (_b = props.routeInfo) === null || _b === void 0 ? void 0 : _b.routeOptions
                        : undefined,
                    currentRouteOptions: child.props.href === ((_c = props.routeInfo) === null || _c === void 0 ? void 0 : _c.pathname)
                        ? (_d = props.routeInfo) === null || _d === void 0 ? void 0 : _d.routeOptions
                        : undefined,
                };
            }
        });
        this.state = {
            tabs,
        };
        this.onTabButtonClick = this.onTabButtonClick.bind(this);
        this.renderTabButton = this.renderTabButton.bind(this);
        this.setActiveTabOnContext = this.setActiveTabOnContext.bind(this);
        this.selectTab = this.selectTab.bind(this);
    }
    componentDidMount() {
        const tabs = this.state.tabs;
        const tabKeys = Object.keys(tabs);
        const activeTab = tabKeys.find((key) => {
            const href = tabs[key].originalHref;
            return this.props.routeInfo.pathname.startsWith(href);
        });
        if (activeTab) {
            this.setState({
                activeTab,
            });
        }
    }
    componentDidUpdate() {
        if (this.state.activeTab) {
            this.setActiveTabOnContext(this.state.activeTab);
        }
    }
    selectTab(tab) {
        const tabUrl = this.state.tabs[tab];
        if (tabUrl) {
            this.onTabButtonClick(new CustomEvent("puxTabButtonClick", {
                detail: {
                    href: tabUrl.currentHref,
                    tab,
                    selected: tab === this.state.activeTab,
                    routeOptions: undefined,
                },
            }));
            return true;
        }
        return false;
    }
    static getDerivedStateFromProps(props, state) {
        var _a, _b, _c;
        const tabs = Object.assign({}, state.tabs);
        const tabKeys = Object.keys(state.tabs);
        const activeTab = tabKeys.find((key) => {
            const href = state.tabs[key].originalHref;
            return props.routeInfo.pathname.startsWith(href);
        });
        // Check to see if the tab button href has changed, and if so, update it in the tabs state
        React__default.Children.forEach(props.children, (child) => {
            if (child != null &&
                typeof child === "object" &&
                child.props &&
                (child.type === PuxTabButton || child.type.isTabButton)) {
                const tab = tabs[child.props.tab];
                if (!tab || tab.originalHref !== child.props.href) {
                    tabs[child.props.tab] = {
                        originalHref: child.props.href,
                        currentHref: child.props.href,
                        originalRouteOptions: child.props.routeOptions,
                        currentRouteOptions: child.props.routeOptions,
                    };
                }
            }
        });
        const { activeTab: prevActiveTab } = state;
        if (activeTab && prevActiveTab) {
            const prevHref = state.tabs[prevActiveTab].currentHref;
            const prevRouteOptions = state.tabs[prevActiveTab].currentRouteOptions;
            if (activeTab !== prevActiveTab ||
                prevHref !== ((_a = props.routeInfo) === null || _a === void 0 ? void 0 : _a.pathname) ||
                prevRouteOptions !== ((_b = props.routeInfo) === null || _b === void 0 ? void 0 : _b.routeOptions)) {
                tabs[activeTab] = {
                    originalHref: tabs[activeTab].originalHref,
                    currentHref: props.routeInfo.pathname + (props.routeInfo.search || ""),
                    originalRouteOptions: tabs[activeTab].originalRouteOptions,
                    currentRouteOptions: (_c = props.routeInfo) === null || _c === void 0 ? void 0 : _c.routeOptions,
                };
                if (props.routeInfo.routeAction === "pop" &&
                    activeTab !== prevActiveTab) {
                    // If navigating back and the tabs change, set the prev tab back to its original href
                    tabs[prevActiveTab] = {
                        originalHref: tabs[prevActiveTab].originalHref,
                        currentHref: tabs[prevActiveTab].originalHref,
                        originalRouteOptions: tabs[prevActiveTab].originalRouteOptions,
                        currentRouteOptions: tabs[prevActiveTab].currentRouteOptions,
                    };
                }
            }
        }
        activeTab && props.onSetCurrentTab(activeTab, props.routeInfo);
        return {
            activeTab,
            tabs,
        };
    }
    onTabButtonClick(e) {
        const tappedTab = this.state.tabs[e.detail.tab];
        const originalHref = tappedTab.originalHref;
        const currentHref = e.detail.href;
        const { activeTab: prevActiveTab } = this.state;
        this.props.onSetCurrentTab(e.detail.tab, this.props.routeInfo);
        // Clicking the current tab will bring you back to the original href
        if (prevActiveTab === e.detail.tab) {
            if (originalHref !== currentHref) {
                this.context.resetTab(e.detail.tab, originalHref, tappedTab.originalRouteOptions);
            }
        }
        else {
            if (this.props.onPuxTabsWillChange) {
                this.props.onPuxTabsWillChange(new CustomEvent("puxTabWillChange", { detail: { tab: e.detail.tab } }));
            }
            if (this.props.onPuxTabsDidChange) {
                this.props.onPuxTabsDidChange(new CustomEvent("puxTabDidChange", { detail: { tab: e.detail.tab } }));
            }
            this.setActiveTabOnContext(e.detail.tab);
            this.context.changeTab(e.detail.tab, currentHref, e.detail.routeOptions);
        }
    }
    renderTabButton(activeTab) {
        return (child) => {
            var _a, _b;
            if (child != null &&
                child.props &&
                (child.type === PuxTabButton || child.type.isTabButton)) {
                const href = child.props.tab === activeTab
                    ? (_a = this.props.routeInfo) === null || _a === void 0 ? void 0 : _a.pathname
                    : this.state.tabs[child.props.tab].currentHref;
                const routeOptions = child.props.tab === activeTab
                    ? (_b = this.props.routeInfo) === null || _b === void 0 ? void 0 : _b.routeOptions
                    : this.state.tabs[child.props.tab].currentRouteOptions;
                return React__default.cloneElement(child, {
                    href,
                    routeOptions,
                    onClick: this.onTabButtonClick,
                });
            }
            return React__default.cloneElement(child, child.props);
        };
    }
    render() {
        const { activeTab } = this.state;
        return (React__default.createElement(PuxTabBarInner, Object.assign({}, this.props, { selectedTab: activeTab }), React__default.Children.map(this.props.children, this.renderTabButton(activeTab))));
    }
    static get contextType() {
        return NavContext;
    }
}
const PuxTabBarContainer = React__default.memo((_a) => {
    var { forwardedRef } = _a, props = __rest$1(_a, ["forwardedRef"]);
    const context = useContext(NavContext);
    return (React__default.createElement(PuxTabBarUnwrapped, Object.assign({ ref: forwardedRef }, props, { routeInfo: props.routeInfo ||
            context.routeInfo || { pathname: window.location.pathname }, onSetCurrentTab: context.setCurrentTab }), props.children));
});
const PuxTabBar = createForwardRef(PuxTabBarContainer, "PuxTabBar");

const PuxBackButton = /*@__PURE__*/ (() => class extends React__default.Component {
    constructor() {
        super(...arguments);
        this.clickButton = (e) => {
            var _a, _b;
            (_b = (_a = this.props).onDidClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            const { defaultHref, routerAnimation } = this.props;
            if (this.context.hasPuxRouter()) {
                e.stopPropagation();
                this.context.goBack(defaultHref, routerAnimation);
            }
            else if (defaultHref !== undefined) {
                window.location.href = defaultHref;
            }
        };
    }
    render() {
        return (React__default.createElement(PuxBackButtonInner, Object.assign({ onClick: this.clickButton }, this.props)));
    }
    static get displayName() {
        return "PuxBackButton";
    }
    static get contextType() {
        return NavContext;
    }
})();

var __rest = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
const PuxItem = /*@__PURE__*/ (() => class extends React__default.Component {
    constructor() {
        super(...arguments);
        this.clickButton = (e) => {
            const { href, routerDirection, routerAnimation, onClick } = this.props;
            //const SCHEME = `(http|ftp|https)://([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?`;
            const SCHEME = /^[a-z][a-z0-9+\-.]*:/;
            onClick && onClick(e);
            if (href != null &&
                href[0] !== "#" &&
                !SCHEME.test(href) &&
                this.context.hasPuxRouter()) {
                e.preventDefault();
                this.context.navigate(href, routerDirection, "push", routerAnimation);
            }
        };
    }
    render() {
        const _a = this.props, { onClick } = _a, rest = __rest(_a, ["onClick"]);
        if (onClick && rest.button === undefined && !rest.href) {
            rest.button = true;
        }
        return React__default.createElement(PuxItemInner, Object.assign({ onClick: this.clickButton }, rest));
    }
    static get displayName() {
        return "PuxItem";
    }
    static get contextType() {
        return NavContext;
    }
})();

export { PuxNavlinks as $, PuxCol as A, PuxContent as B, PuxDatetime as C, DefaultPuxLifeCycleContext as D, PuxDropdown as E, PuxFab as F, PuxFabButton as G, PuxFabList as H, PuxGauge as I, PuxGrid as J, PuxHeader as K, PuxImg as L, PuxInfiniteScroll as M, NavContext as N, PuxInfiniteScrollContent as O, PuxFooter as P, PuxInput as Q, PuxItemDivider as R, StackContext as S, PuxItemGroup as T, PuxLabel as U, PuxList as V, PuxListHeader as W, PuxMenu as X, PuxMenuButton as Y, PuxMenuToggle as Z, PuxNav as _, attachProps as a, PuxNote as a0, PuxPageOutlet as a1, PuxProgressBar as a2, PuxRadio as a3, PuxRadioGroup as a4, PuxRange as a5, PuxRefresher as a6, PuxRefresherContent as a7, PuxRow as a8, PuxSearchbar as a9, PuxSegment as aa, PuxSegmentButton as ab, PuxSelect as ac, PuxSelectOption as ad, PuxSelectPopover as ae, PuxSimcard as af, PuxSkeletonText as ag, PuxSlide as ah, PuxSlides as ai, PuxSpinner as aj, PuxSplitPane as ak, PuxTable as al, PuxText as am, PuxTextarea as an, PuxThumbnail as ao, PuxTitle as ap, PuxToggle as aq, PuxToolbar as ar, PuxVirtualScroll as as, PuxIcon as at, PuxLogo as au, PuxPage as av, PuxTabButton as aw, PuxBackButton as ax, PuxItem as ay, PuxButton as b, PuxDialogTitle as c, PuxModalContent as d, PuxSheet as e, PuxLifeCycleContext as f, PuxRouterOutletInner as g, createForwardRef as h, PuxTabBar as i, PageManager$1 as j, PuxAccordian as k, PuxAccordianPanel as l, PuxActionButton as m, PuxApp as n, PuxAvatar as o, PuxBackdrop as p, PuxBadge as q, PuxButtons as r, PuxCard as s, PuxCardContent as t, PuxCardHeader as u, PuxCardSubtitle as v, PuxCardTitle as w, PuxChart as x, PuxCheckbox as y, PuxChip as z };
//# sourceMappingURL=PuxItem-89181e03.esm.js.map
